/**
 * RangeCalculator - D&D 5e Range and Reach Calculations
 * date_of_change: 2025-08-30
 * 
 * Handles proper distance calculations for:
 * - Melee attacks and reach weapons
 * - Ranged attacks and thrown weapons
 * - Spell ranges and areas of effect
 * - Movement and positioning
 * - Token size and space considerations
 * 
 * Supports both square and hexagonal grids with accurate D&D 5e rules.
 */

class RangeCalculator {
  /**
   * Parse range strings that include dimensional qualifiers (e.g., "Self (100-foot line)").
   * Returns the longest numeric distance in feet if present, otherwise null.
   * @date_of_change 2025-10-12 - Support Lightning Bolt style ranges for AoE validation
   */
  static extractRangeFromString(rangeText) {
    if (typeof rangeText !== 'string') return null;
    const matches = Array.from(rangeText.matchAll(/(\d+)(?:\s*-?\s*)(foot|feet|ft|mile|miles)/gi));
    if (!matches.length) return null;
    const values = matches.map(match => {
      const rawValue = parseInt(match[1], 10);
      const unit = match[2]?.toLowerCase() || 'foot';
      return unit.includes('mile') ? rawValue * 5280 : rawValue;
    });
    return values.length ? Math.max(...values) : null;
  }

  /**
   * D&D 5e Size categories and their space in feet
   */
  static SIZES = {
    'Tiny': 2.5,
    'Small': 5,
    'Medium': 5,
    'Large': 10,
    'Huge': 15,
    'Gargantuan': 20
  };

  /**
   * Standard weapon ranges in feet
   */
  static WEAPON_RANGES = {
    // Melee weapons
    'melee': 5,
    'reach': 10,
    
    // Ranged weapons (normal/long range)
    'shortbow': [80, 320],
    'longbow': [150, 600],
    'crossbow': [80, 320],
    'handaxe': [20, 60],
    'javelin': [30, 120],
    'dart': [20, 60],
    'dagger': [20, 60],
    'spear': [20, 60]
  };

  /**
   * Calculate grid distance between two positions
   * @param {Object} pos1 - First position {x, y} in grid coordinates
   * @param {Object} pos2 - Second position {x, y} in grid coordinates
   * @param {string} gridType - 'square' or 'hex'
   * @param {number} feetPerCell - Feet per grid cell (default 5)
   * @returns {Object} - Distance info with cells, feet, and diagonal info
   */
  // @date_of_change 2025-10-13 - POLICY: Require explicit gridType/feetPerCell; no defaults
  // rule_reference: AGENTS.md §9 NO LEGACY PATHS OR FALLBACKS
  static calculateGridDistance(pos1, pos2, gridType, feetPerCell) {
    if (!gridType || !Number.isFinite(feetPerCell)) {
      throw new Error('[RangeCalculator] calculateGridDistance: missing gridType/feetPerCell (provide explicit context)');
    }
    const dx = Math.abs(pos2.x - pos1.x);
    const dy = Math.abs(pos2.y - pos1.y);

    let cells, feet, isDiagonal = false;

    if (gridType === 'square') {
      // D&D 5e uses "every other diagonal counts as 10 feet" rule
      const straight = Math.min(dx, dy);
      const remaining = Math.max(dx, dy) - straight;
      
      // First diagonal costs 5 feet, second costs 10 feet, etc.
      // Simplified: every 2 diagonals = 15 feet (5+10), so 7.5 feet per diagonal average
      // But for game simplicity, we'll use the common variant: diagonals = 1.5x
      
      // Standard 5e rule: First diagonal = 5ft, second = 10ft, alternating
      let diagonalFeet = 0;
      for (let i = 0; i < straight; i++) {
        diagonalFeet += (i % 2 === 0) ? 5 : 10;
      }

      feet = diagonalFeet + (remaining * feetPerCell);
      cells = Math.max(dx, dy); // Chebyshev distance for movement
      isDiagonal = dx > 0 && dy > 0;
    } else if (gridType === 'hex') {
      // Hexagonal grids have uniform distance
      // Use axial coordinate conversion for accurate hex distance
      const q1 = pos1.x;
      const r1 = pos1.y - Math.floor((pos1.x + (pos1.x & 1)) / 2);
      const q2 = pos2.x;
      const r2 = pos2.y - Math.floor((pos2.x + (pos2.x & 1)) / 2);
      
      cells = Math.max(Math.abs(q1 - q2), Math.abs(r1 - r2), Math.abs(q1 + r1 - q2 - r2));
      feet = cells * feetPerCell;
    } else {
      // @date_of_change 2025-10-13 - Remove Euclidean fallback; unknown grid is an error
      throw new Error(`[RangeCalculator] Unsupported gridType: ${gridType}`);
    }

    return {
      cells: Math.ceil(cells),
      feet: Math.ceil(feet),
      isDiagonal,
      horizontal: dx,
      vertical: dy
    };
  }

  /**
   * Helper: Compute edge-to-edge distance in feet between two tokens.
   */
  static getEdgeDistanceFeet(attacker, target, options = {}) {
    // @date_of_change 2025-10-13 - POLICY: Require explicit grid context
    // rule_reference: AGENTS.md §9 NO LEGACY PATHS OR FALLBACKS
    const { gridType, feetPerCell } = options;
    if (!gridType || !Number.isFinite(feetPerCell)) {
      throw new Error('[RangeCalculator] getEdgeDistanceFeet: missing gridType/feetPerCell');
    }
    const attackerPos = this.getTokenCenter(attacker, options);
    const targetPos = this.getTokenCenter(target, options);

    // @date_of_change 2025-09-14 - ENHANCED DEBUG: Track distance calculation details
    console.log(`[RangeCalculator] 📏 DISTANCE_CALCULATION_DEBUG:`, {
      attackerName: attacker?.name,
      targetName: target?.name,
      attackerRawPos: { x: attacker?.x, y: attacker?.y, grid: attacker?.grid, gridX: attacker?.gridX, gridY: attacker?.gridY },
      targetRawPos: { x: target?.x, y: target?.y, grid: target?.grid, gridX: target?.gridX, gridY: target?.gridY },
      attackerCenter: attackerPos,
      targetCenter: targetPos,
      gridSize: options?.gridSize,
      feetPerCell
    });

    const centerDistance = this.calculateGridDistance(attackerPos, targetPos, gridType, feetPerCell);
    const attackerSizeFt = this.SIZES[attacker?.size] || 5;
    const targetSizeFt = this.SIZES[target?.size] || 5;
    const radii = (attackerSizeFt / 2) + (targetSizeFt / 2);
    const edgeFeet = Math.max(0, centerDistance.feet - radii);

    console.log(`[RangeCalculator] 📏 DISTANCE_RESULT: ${edgeFeet} feet (center: ${centerDistance.feet}, radii: ${radii})`);

    return { edgeFeet, centerDistance };
  }

  /**
   * Explicit melee range check.
   */
  static checkMeleeRange(attacker, target, params = {}, options = {}) {
    // @date_of_change 2025-10-14 - FIX: Grid-accurate reach using token footprints (Chebyshev)
    // rule_reference: combatsimulartorrules.md — Treat reach in squares: 5 ft → adjacent (1), 10 ft → two squares (2)
    // POLICY: Do not default reach; require explicit
    const reachFeet = Number.isFinite(params.reachFeet) ? params.reachFeet : null;
    if (!Number.isFinite(reachFeet)) {
      return { kind: 'melee', inRange: false, distance: null, reachFeet: null, distanceInfo: null, reason: 'Missing reachFeet parameter' };
    }
    // Require explicit grid context for footprint math
    const gridSize = Number(options.gridSize);
    const feetPerCell = Number(options.feetPerCell);
    if (!Number.isFinite(gridSize) || !Number.isFinite(feetPerCell)) {
      return { kind: 'melee', inRange: false, distance: null, reachFeet, distanceInfo: null, reason: 'Missing gridSize/feetPerCell' };
    }

    // Convert reach to whole-cell distance (Chebyshev metric)
    const reachCells = Math.max(0, Math.ceil(reachFeet / feetPerCell));

    let minCellDist = Infinity;
    try {
      const attackerCells = this.getTokenCells(attacker, options);
      const targetCells = this.getTokenCells(target, options);
      for (const a of attackerCells) {
        for (const t of targetCells) {
          const dx = Math.abs(a.x - t.x);
          const dy = Math.abs(a.y - t.y);
          const chebyshev = Math.max(dx, dy);
          if (chebyshev < minCellDist) minCellDist = chebyshev;
        }
      }
    } catch (e) {
      return { kind: 'melee', inRange: false, distance: null, reachFeet, distanceInfo: null, reason: `Footprint computation failed: ${e?.message || e}` };
    }

    const inRange = Number.isFinite(minCellDist) && (minCellDist <= reachCells);
    const distanceFeet = Number.isFinite(minCellDist) ? (minCellDist * feetPerCell) : null;
    return { kind: 'melee', inRange, distance: distanceFeet, reachFeet, distanceInfo: { cells: minCellDist, feet: distanceFeet } };
  }

  /**
   * Explicit ranged range check with bands.
   */
  static checkRangedRange(attacker, target, params = {}, options = {}) {
    // @date_of_change 2025-10-13 - POLICY: Support either (normal+long) or maxRange; no defaults
    const normalRange = Number.isFinite(params.normalRange) ? params.normalRange : undefined;
    const longRange = Number.isFinite(params.longRange) ? params.longRange : undefined;
    const maxRange = Number.isFinite(params.maxRange) ? params.maxRange : undefined;
    const { edgeFeet, centerDistance } = this.getEdgeDistanceFeet(attacker, target, options);
    if (Number.isFinite(normalRange) && Number.isFinite(longRange)) {
      const inRange = edgeFeet <= longRange;
      const hasDisadvantage = edgeFeet > normalRange && edgeFeet <= longRange;
      const band = !inRange ? 'out' : (hasDisadvantage ? 'long' : 'short');
      return { kind: 'ranged', inRange, hasDisadvantage, band, distance: edgeFeet, normalRange, longRange, distanceInfo: { ...centerDistance, feet: edgeFeet } };
    }
    if (Number.isFinite(maxRange)) {
      const inRange = edgeFeet <= maxRange;
      return { kind: 'ranged', inRange, hasDisadvantage: false, band: inRange ? 'short' : 'out', distance: edgeFeet, maxRange, distanceInfo: { ...centerDistance, feet: edgeFeet } };
    }
    return { kind: 'ranged', inRange: false, hasDisadvantage: false, band: 'out', distance: edgeFeet, normalRange: normalRange ?? null, longRange: longRange ?? null, distanceInfo: { ...centerDistance, feet: edgeFeet }, reason: 'Missing range parameters (normal/long or maxRange)' };
  }

  /**
   * Explicit attack kind selector.
   */
  static checkAttackRange(kind, attacker, target, params = {}, options = {}) {
    const k = String(kind || '').toLowerCase();
    if (k === 'melee') return this.checkMeleeRange(attacker, target, params, options);
    if (k === 'ranged') return this.checkRangedRange(attacker, target, params, options);
    return { kind: k || null, inRange: false, reason: 'Unknown attack kind' };
  }

  /**
   * Calculate if target is within melee range considering token sizes
   * @param {Object} attacker - Attacker token with position and size
   * @param {Object} target - Target token with position and size
   * @param {Object} options - Options {weaponReach: 5, gridType: 'square', feetPerCell: 5}
   * @returns {boolean} - True if target is within reach
   */
  static isWithinMeleeRange(attacker, target, options = {}) {
    // @date_of_change 2025-10-13 - POLICY: Require explicit weaponReach/gridType/feetPerCell
    const { weaponReach, gridType, feetPerCell, includeTokenSize = true } = options;
    if (!Number.isFinite(weaponReach) || !gridType || !Number.isFinite(feetPerCell)) {
      throw new Error('[RangeCalculator] isWithinMeleeRange: missing weaponReach/gridType/feetPerCell');
    }

    // Get token positions (center of token)
    const attackerPos = this.getTokenCenter(attacker, options);
    const targetPos = this.getTokenCenter(target, options);

    // Calculate center-to-center distance in feet
    const distance = this.calculateGridDistance(attackerPos, targetPos, gridType, feetPerCell);

    // Convert to edge-to-edge distance by subtracting half-sizes (radii)
    let edgeFeet = distance.feet;
    if (includeTokenSize) {
      const attackerSizeFt = this.SIZES[attacker.size] || 5;
      const targetSizeFt = this.SIZES[target.size] || 5;
      const radii = (attackerSizeFt / 2) + (targetSizeFt / 2);
      edgeFeet = Math.max(0, edgeFeet - radii);
    }

    return edgeFeet <= weaponReach;
  }

  /**
   * Calculate if target is within ranged attack range
   * @param {Object} attacker - Attacker token with position
   * @param {Object} target - Target token with position
   * @param {Object} weapon - Weapon with range properties
   * @param {Object} options - Grid options
   * @returns {Object} - Range info with inRange, disadvantage, distance
   */
  static isWithinRangedRange(attacker, target, weapon, options = {}) {
    // @date_of_change 2025-10-13 - POLICY: Require explicit grid context and structured ranges
    const { gridType, feetPerCell } = options;
    if (!gridType || !Number.isFinite(feetPerCell)) {
      throw new Error('[RangeCalculator] isWithinRangedRange: missing gridType/feetPerCell');
    }

    const attackerPos = this.getTokenCenter(attacker, options);
    const targetPos = this.getTokenCenter(target, options);
    
    const centerDistance = this.calculateGridDistance(attackerPos, targetPos, gridType, feetPerCell);
    // Use edge-to-edge distance for ranged as well
    const attackerSizeFt = this.SIZES[attacker.size] || 5;
    const targetSizeFt = this.SIZES[target.size] || 5;
    const radii = (attackerSizeFt / 2) + (targetSizeFt / 2);
    const edgeFeet = Math.max(0, centerDistance.feet - radii);

    // Get weapon range from structured inputs only
    let { normalRange, longRange, maxRange, hasStructuredRange: haveRange } = this.resolveRangeBands(weapon?.range);

    if (!haveRange) {
      ({ normalRange, longRange, maxRange, hasStructuredRange: haveRange } = this.resolveRangeBands({
        normal: weapon?.normalRange,
        long: weapon?.longRange,
        maxRange: weapon?.maxRange ?? weapon?.rangeFeet ?? null
      }));
    }

    if (!haveRange && weapon?.type && this.WEAPON_RANGES[weapon.type]) {
      const weaponRanges = this.WEAPON_RANGES[weapon.type];
      normalRange = Array.isArray(weaponRanges) ? weaponRanges[0] : weaponRanges;
      longRange = Array.isArray(weaponRanges) ? weaponRanges[1] : undefined;
      haveRange = Number.isFinite(normalRange) && Number.isFinite(longRange);
    }

    if (!haveRange) {
      return {
        inRange: false,
        hasDisadvantage: false,
        distance: edgeFeet,
        normalRange: normalRange ?? null,
        longRange: longRange ?? null,
        maxRange: maxRange ?? null,
        distanceInfo: { ...centerDistance, feet: edgeFeet },
        reason: 'No structured range defined for ranged attack'
      };
    }

    if (Number.isFinite(normalRange) && Number.isFinite(longRange)) {
      const inRange = edgeFeet <= longRange;
      const hasDisadvantage = edgeFeet > normalRange && edgeFeet <= longRange;
      return {
        inRange,
        hasDisadvantage,
        distance: edgeFeet,
        normalRange,
        longRange,
        distanceInfo: { ...centerDistance, feet: edgeFeet }
      };
    }

    // Use maxRange only (spells or single-valued weapons)
    const inRange = edgeFeet <= maxRange;
    return {
      inRange,
      hasDisadvantage: false,
      distance: edgeFeet,
      maxRange,
      distanceInfo: { ...centerDistance, feet: edgeFeet }
    };
  }

  /**
   * Calculate spell range and area of effect
   * @param {Object} caster - Caster token with position
   * @param {Object} spell - Spell with range and area properties
   * @param {Object} targetPoint - Target point {x, y} in grid coordinates
   * @param {Object} options - Grid options
   * @returns {Object} - Spell range validation and affected area
   */
  static calculateSpellRange(caster, spell, targetPoint, options = {}) {
    // @date_of_change 2025-10-13 - POLICY: Require explicit grid context and structured spell.range
    const { gridType, feetPerCell } = options;
    if (!gridType || !Number.isFinite(feetPerCell)) {
      throw new Error('[RangeCalculator] calculateSpellRange: missing gridType/feetPerCell');
    }

    const casterPos = this.getTokenCenter(caster, options);
    const distance = this.calculateGridDistance(casterPos, targetPoint, gridType, feetPerCell);

    // Parse spell range with robust string fallback
    let spellRange;
    if (typeof spell?.range === 'string') {
      const lower = spell.range.toLowerCase().trim();
      if (lower === 'self' || lower.startsWith('self')) {
        spellRange = 0;
      } else if (lower === 'touch') {
        spellRange = 5;
      } else {
        const parsed = this.parseRangeValue(spell.range);
        if (Number.isFinite(parsed)) spellRange = parsed;
      }
    } else if (spell?.range === 'self') {
      spellRange = 0;
    } else if (spell?.range === 'touch') {
      spellRange = 5;
    } else if (Number.isFinite(spell?.range)) {
      spellRange = Number(spell.range);
    } else if (Number.isFinite(spell?.rangeFeet)) {
      spellRange = Number(spell.rangeFeet);
    } else {
      const parsed = this.parseRangeValue(spell?.rangeText ?? spell?.rangeFeet);
      if (Number.isFinite(parsed)) {
        spellRange = parsed;
      } else {
        return { inRange: false, distance: null, spellRange: null, distanceInfo: null, affectedArea: [], reason: 'Invalid or missing spell.range (require number, "self", or "touch")' };
      }
    }

    const inRange = distance.feet <= spellRange;

    // Calculate area of effect if applicable
    let affectedArea = [];
    if (spell.areaOfEffect) {
      try {
        affectedArea = this.calculateAreaOfEffect(targetPoint, spell.areaOfEffect, gridType, feetPerCell);
      } catch (e) {
        return { inRange: false, distance: distance.feet, spellRange, distanceInfo: distance, affectedArea: [], reason: e.message };
      }
    }

    return {
      inRange,
      distance: distance.feet,
      spellRange,
      distanceInfo: distance,
      affectedArea
    };
  }

  /**
   * Calculate area of effect for spells and abilities with corner vs center targeting precision
   * @param {Object} centerPoint - Center point {x, y} in grid coordinates
   * @param {Object} areaOfEffect - AOE definition {shape, size}
   * @param {string} gridType - 'square' or 'hex'
   * @param {number} feetPerCell - Feet per grid cell
   * @param {string} targetMode - 'center' or 'corner' for precise grid targeting
   * @returns {Array} - Array of affected grid coordinates
   * @date_of_change 2025-09-15 - Enhanced for corner vs center targeting precision
   */
  // @date_of_change 2025-10-13 - POLICY: Require explicit grid context; no defaults; line/cone need direction
  static calculateAreaOfEffect(centerPoint, areaOfEffect, gridType, feetPerCell, targetMode = 'center') {
    if (!gridType || !Number.isFinite(feetPerCell)) {
      throw new Error('[RangeCalculator] calculateAreaOfEffect: missing gridType/feetPerCell');
    }
    const { shape, size } = areaOfEffect;
    const affectedCells = [];

    const radiusInCells = Math.ceil(size / feetPerCell);

    switch (shape.toLowerCase()) {
      case 'sphere':
      case 'circle':
        // @date_of_change 2025-09-15 - Enhanced circular AoE with corner vs center precision
        // Corner targeting: full radius from grid intersection
        // Center targeting: radius from center of square

        for (let x = centerPoint.x - radiusInCells; x <= centerPoint.x + radiusInCells; x++) {
          for (let y = centerPoint.y - radiusInCells; y <= centerPoint.y + radiusInCells; y++) {

            // Calculate distance based on targeting mode
            let distance;
            if (targetMode === 'corner') {
              // Corner targeting: measure from grid intersection (exact point)
              // This allows full radius expansion in all directions
              distance = this.calculateGridDistance(centerPoint, {x, y}, gridType, feetPerCell);
            } else {
              // Center targeting: measure from center of target square to center of affected square
              // This reduces effective radius by 0.5 cells since we're measuring center-to-center
              const adjustedCenter = {
                x: centerPoint.x + 0.5,
                y: centerPoint.y + 0.5
              };
              const adjustedTarget = {
                x: x + 0.5,
                y: y + 0.5
              };
              distance = this.calculateGridDistance(adjustedCenter, adjustedTarget, gridType, feetPerCell);
            }

            if (distance.feet <= size) {
              affectedCells.push({x, y, distance: distance.feet, targetMode});
            }
          }
        }
        break;

      case 'cube':
      case 'square':
        // Square area of effect
        const halfSize = Math.floor(radiusInCells / 2);
        for (let x = centerPoint.x - halfSize; x <= centerPoint.x + halfSize; x++) {
          for (let y = centerPoint.y - halfSize; y <= centerPoint.y + halfSize; y++) {
            affectedCells.push({x, y, distance: 0});
          }
        }
        break;

      case 'line':
        // @date_of_change 2025-10-13 - POLICY: Direction required for lines; fail-fast
        throw new Error('[RangeCalculator] calculateAreaOfEffect: line requires directional template (use calculateLineAreaCells)');

      case 'cone':
        // @date_of_change 2025-10-13 - POLICY: Direction required for cones; fail-fast
        throw new Error('[RangeCalculator] calculateAreaOfEffect: cone requires directional template');

      default:
        // Unknown shape: default to center cell only
        affectedCells.push({ x: centerPoint.x, y: centerPoint.y, distance: 0 });
        break;
    }

    return affectedCells;
  }

  /**
   * Compute the pixel and grid edge origin for directional AoE templates (cones/lines).
   * Implements PHB (2024) Lightning Bolt requirements by casting from the token's facing edge.
   * @param {Object} casterToken - Token performing the AoE (expects x,y,width,height or sizeCells)
   * @param {number} directionDegrees - Direction the template is pointing (degrees, 0 = east)
   * @param {Object} options - { gridSize }
   * @returns {{ center: {x:number,y:number}, edge: { pixel: {x:number,y:number}, grid: {x:number,y:number} } }}
   * @date_of_change 2025-10-11 - Align directional AoE origins with rules-as-written token geometry
   */
  /**
   * Determine the caster-centered origin for directional templates.
   * @date_of_change 2025-10-11
   * @rule_reference PHB 2024, Lightning Bolt – line originates from caster’s edge toward target.
   */
  static getDirectionalAoEOrigin(casterToken, directionDegrees, options = {}) {
    // @date_of_change 2025-10-13 - POLICY: Require explicit gridSize
    const gridSize = Number(options.gridSize);
    if (!Number.isFinite(gridSize)) {
      throw new Error('[RangeCalculator] getDirectionalAoEOrigin: missing gridSize');
    }
    if (!casterToken) {
      return {
        center: { x: 0, y: 0 },
        edge: { pixel: { x: 0, y: 0 }, grid: { x: 0, y: 0 } }
      };
    }

    let tokenWidth = Number(casterToken.width) || gridSize;
    let tokenHeight = Number(casterToken.height) || gridSize;

    if (casterToken.sizeCells && casterToken.sizeCells.w && casterToken.sizeCells.h) {
      tokenWidth = Number(casterToken.sizeCells.w) * gridSize;
      tokenHeight = Number(casterToken.sizeCells.h) * gridSize;
    }

    const casterCenterX = Number(casterToken.x) + (tokenWidth / 2);
    const casterCenterY = Number(casterToken.y) + (tokenHeight / 2);

    const directionRad = (Number(directionDegrees) || 0) * Math.PI / 180;
    const absX = Math.abs(Math.cos(directionRad));
    const absY = Math.abs(Math.sin(directionRad));
    const tokenRadiusX = tokenWidth / 2;
    const tokenRadiusY = tokenHeight / 2;

    let edgeDistance;
    if (tokenRadiusX === tokenRadiusY) {
      edgeDistance = tokenRadiusX;
    } else {
      const denom = Math.sqrt(
        tokenRadiusY * tokenRadiusY * absX * absX +
        tokenRadiusX * tokenRadiusX * absY * absY
      ) || 1;
      edgeDistance = (tokenRadiusX * tokenRadiusY) / denom;
    }

    const edgePixelX = casterCenterX + Math.cos(directionRad) * edgeDistance;
    const edgePixelY = casterCenterY + Math.sin(directionRad) * edgeDistance;

    return {
      center: { x: casterCenterX, y: casterCenterY },
      edge: {
        pixel: { x: edgePixelX, y: edgePixelY },
        grid: { x: edgePixelX / gridSize, y: edgePixelY / gridSize }
      }
    };
  }

  /**
   * Calculate grid cells covered by a directional line AoE (e.g., Lightning Bolt 100 ft. line).
   * Applies PHB (2024) template rules: 5 ft. wide line, sampled per grid cell along its length.
   * @param {{x:number,y:number}} originGrid - Edge-origin in grid coordinates (can be fractional)
   * @param {number} directionDegrees - Direction of travel in degrees (0 = east)
   * @param {number} lengthFeet - Total length of the line in feet
   * @param {number} widthFeet - Width of the line in feet (default 5)
   * @param {Object} options - { feetPerCell }
   * @returns {Array<{x:number,y:number}>} unique grid cells affected
   * @date_of_change 2025-10-11 - Provide deterministic Lightning Bolt coverage for BattleRoom
   */
  /**
   * Enumerate grid cells intersected by a straight AoE line.
   * @date_of_change 2025-10-11
   * @rule_reference PHB 2024, Lightning Bolt template dimensions.
   */
  static calculateLineAreaCells(originGrid, directionDegrees, lengthFeet, widthFeet = 5, options = {}) {
    // @date_of_change 2025-10-13 - POLICY: Require explicit feetPerCell
    const feetPerCell = Number(options.feetPerCell);
    if (!Number.isFinite(feetPerCell)) {
      throw new Error('[RangeCalculator] calculateLineAreaCells: missing feetPerCell');
    }
    const lengthCells = Math.max(1, Math.ceil(Number(lengthFeet) / feetPerCell));
    const widthCells = Math.max(1, Math.ceil(Number(widthFeet) / feetPerCell));

    const directionRad = (Number(directionDegrees) || 0) * Math.PI / 180;
    const stepX = Math.cos(directionRad);
    const stepY = Math.sin(directionRad);
    const perpRad = directionRad + Math.PI / 2;
    const perpStepX = Math.cos(perpRad);
    const perpStepY = Math.sin(perpRad);

    const originX = Number(originGrid?.x) || 0;
    const originY = Number(originGrid?.y) || 0;

    const unique = new Map();

    const halfWidth = (widthCells - 1) / 2;
    for (let i = 0; i < lengthCells; i += 1) {
      const sampleX = originX + stepX * (i + 0.5);
      const sampleY = originY + stepY * (i + 0.5);

      for (let w = 0; w < widthCells; w += 1) {
        const offset = w - halfWidth;
        const offsetX = sampleX + perpStepX * offset;
        const offsetY = sampleY + perpStepY * offset;

        const cellX = Math.floor(offsetX);
        const cellY = Math.floor(offsetY);
        const key = `${cellX},${cellY}`;

        if (!unique.has(key)) {
          unique.set(key, { x: cellX, y: cellY });
        }
      }
    }

    return Array.from(unique.values());
  }

  /**
   * Calculate grid cells covered by a directional cone AoE (90° default, like Burning Hands, Cone of Cold).
   * Matches the AoE overlay calculation so visuals and engine stay in sync.
   * @param {{x:number,y:number}} originGrid - Edge-origin in grid coordinates (can be fractional)
   * @param {number} directionDegrees - Direction the cone points (0 = east)
   * @param {number} sizeFeet - Cone length in feet
   * @param {Object} options - { feetPerCell, halfAngleDegrees? }
   * @returns {Array<{x:number,y:number}>} unique grid cells affected
   * @date_of_change 2025-10-13
   * @rule_reference D&D 5e cones; overlay uses a 90° cone (±45° half-angle)
   */
  static calculateConeAreaCells(originGrid, directionDegrees, sizeFeet, options = {}) {
    // @date_of_change 2025-10-13 - POLICY: Require explicit feetPerCell
    const feetPerCell = Number(options.feetPerCell);
    if (!Number.isFinite(feetPerCell)) {
      throw new Error('[RangeCalculator] calculateConeAreaCells: missing feetPerCell');
    }

    const rangeCells = Math.max(1, Math.floor(Number(sizeFeet) / feetPerCell));
    const halfAngleDeg = Number.isFinite(options.halfAngleDegrees) ? Number(options.halfAngleDegrees) : 45; // 90° cone

    const dirDeg = Number(directionDegrees) || 0;
    const originX = Number(originGrid?.x) || 0;
    const originY = Number(originGrid?.y) || 0;

    // Enumerate candidate cells around the origin
    const minX = Math.floor(originX - rangeCells);
    const maxX = Math.ceil(originX + rangeCells);
    const minY = Math.floor(originY - rangeCells);
    const maxY = Math.ceil(originY + rangeCells);

    const unique = new Map();
    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        const dx = x - Math.floor(originX);
        const dy = y - Math.floor(originY);
        // Skip the origin cell
        if (dx === 0 && dy === 0) continue;

        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance > rangeCells) continue;

        // Angle test with wrap-around handling
        const cellAngle = Math.atan2(dy, dx) * 180 / Math.PI;
        let diff = Math.abs(cellAngle - dirDeg);
        if (diff > 180) diff = 360 - diff;
        if (diff <= halfAngleDeg) {
          const key = `${x},${y}`;
          if (!unique.has(key)) unique.set(key, { x, y });
        }
      }
    }

    return Array.from(unique.values());
  }

  /**
   * Get the center position of a token considering its size
   * @param {Object} token - Token with position and size
   * @returns {Object} - Center position {x, y} in grid coordinates
   */
  static getTokenCenter(token, options = {}) {
    // @date_of_change 2025-10-13 - POLICY: Require explicit gridSize/feetPerCell
    const feetPerCell = Number(options.feetPerCell);
    const gridSizePx = Number(options.gridSize);
    if (!Number.isFinite(gridSizePx) || !Number.isFinite(feetPerCell)) {
      throw new Error('[RangeCalculator] getTokenCenter: missing gridSize/feetPerCell');
    }

    // If explicit grid coordinates exist, use them as top-left cell then offset by size.
    // Token.js renders nested token.grid first, so range math must prefer that same source of truth.
    // date_of_change: 2026-06-23
    const nestedGridX = Number(token?.grid?.x);
    const nestedGridY = Number(token?.grid?.y);
    const legacyGridX = Number(token?.gridX);
    const legacyGridY = Number(token?.gridY);
    if (
      (Number.isFinite(nestedGridX) && Number.isFinite(nestedGridY)) ||
      (Number.isFinite(legacyGridX) && Number.isFinite(legacyGridY))
    ) {
      let centerX = Number.isFinite(nestedGridX) ? nestedGridX : legacyGridX;
      let centerY = Number.isFinite(nestedGridY) ? nestedGridY : legacyGridY;
      const tokenSizeFt = this.SIZES[token?.size] || 5;
      if (tokenSizeFt > feetPerCell) {
        const sizeInCells = Math.ceil(tokenSizeFt / feetPerCell);
        centerX += Math.floor(sizeInCells / 2);
        centerY += Math.floor(sizeInCells / 2);
      }
      return { x: centerX, y: centerY };
    }

    // Otherwise, assume x/y are pixels and convert to grid cells using gridSize
    const xPx = Number(token?.x ?? token?.position?.x ?? 0);
    const yPx = Number(token?.y ?? token?.position?.y ?? 0);
    const wPx = Number(token?.width ?? token?.size?.w ?? gridSizePx);
    const hPx = Number(token?.height ?? token?.size?.h ?? gridSizePx);

    const cxPx = xPx + (wPx / 2);
    const cyPx = yPx + (hPx / 2);

    const centerX = Math.round(cxPx / gridSizePx);
    const centerY = Math.round(cyPx / gridSizePx);
    return { x: centerX, y: centerY };
  }

  /**
   * Enumerate the grid cells occupied by a token (its footprint), top-left anchored.
   * Uses explicit grid context. Prefers token.grid, then token.gridX/gridY if available; otherwise derives
   * top-left from pixel coordinates. Size in cells is computed from token.size (Tiny/Small/...),
   * width/height in pixels, or sizeCells when provided.
   * @param {Object} token
   * @param {Object} options - { gridSize: number, feetPerCell: number }
   * @returns {Array<{x:number,y:number}>}
   * @date_of_change 2025-10-14 - Added to support footprint-based AoE target inclusion
   */
  static getTokenCells(token, options = {}) {
    const gridSizePx = Number(options.gridSize);
    const feetPerCell = Number(options.feetPerCell);
    if (!Number.isFinite(gridSizePx) || !Number.isFinite(feetPerCell)) {
      throw new Error('[RangeCalculator] getTokenCells: missing gridSize/feetPerCell');
    }

    // Resolve top-left grid coordinate
    let topLeftX;
    let topLeftY;
    const nestedGridX = Number(token?.grid?.x);
    const nestedGridY = Number(token?.grid?.y);
    const legacyGridX = Number(token?.gridX);
    const legacyGridY = Number(token?.gridY);
    if (
      (Number.isFinite(nestedGridX) && Number.isFinite(nestedGridY)) ||
      (Number.isFinite(legacyGridX) && Number.isFinite(legacyGridY))
    ) {
      // Match Token.js rendering: nested token.grid is the canonical rendered position when present.
      // date_of_change: 2026-06-23
      topLeftX = Math.floor(Number.isFinite(nestedGridX) ? nestedGridX : legacyGridX);
      topLeftY = Math.floor(Number.isFinite(nestedGridY) ? nestedGridY : legacyGridY);
    } else {
      // Derive from pixel position
      const xPx = Number(token?.x ?? token?.position?.x ?? 0);
      const yPx = Number(token?.y ?? token?.position?.y ?? 0);
      topLeftX = Math.floor(xPx / gridSizePx);
      topLeftY = Math.floor(yPx / gridSizePx);
    }

    // Determine size in cells
    let widthCells = 1;
    let heightCells = 1;

    // Prefer explicit sizeCells if provided
    if (token?.sizeCells && Number.isFinite(token.sizeCells.w) && Number.isFinite(token.sizeCells.h)) {
      widthCells = Math.max(1, Math.ceil(Number(token.sizeCells.w)));
      heightCells = Math.max(1, Math.ceil(Number(token.sizeCells.h)));
    } else {
      // Compute from token.size category or pixel width/height
      const tokenSizeFt = this.SIZES[token?.size] || 5;
      const fromCategoryCells = Math.max(1, Math.ceil(tokenSizeFt / feetPerCell));

      // If width/height in pixels available, compute from those too
      const wPx = Number(token?.width);
      const hPx = Number(token?.height);
      const fromPixelsW = Number.isFinite(wPx) ? Math.max(1, Math.ceil(wPx / gridSizePx)) : fromCategoryCells;
      const fromPixelsH = Number.isFinite(hPx) ? Math.max(1, Math.ceil(hPx / gridSizePx)) : fromCategoryCells;

      widthCells = Math.max(fromCategoryCells, fromPixelsW);
      heightCells = Math.max(fromCategoryCells, fromPixelsH);
    }

    const cells = [];
    for (let dx = 0; dx < widthCells; dx++) {
      for (let dy = 0; dy < heightCells; dy++) {
        cells.push({ x: topLeftX + dx, y: topLeftY + dy });
      }
    }
    return cells;
  }

  /**
   * Check if there's line of sight between two tokens
   * @param {Object} source - Source token
   * @param {Object} target - Target token
   * @param {Array} obstacles - Array of obstacle positions or tokens
   * @param {Object} options - Options for line of sight calculation
   * @returns {boolean} - True if line of sight exists
   */
  static hasLineOfSight(source, target, obstacles = [], options = {}) {
    const sourcePos = this.getTokenCenter(source, options);
    const targetPos = this.getTokenCenter(target, options);

    // Simple line-of-sight using Bresenham's line algorithm
    const points = this.getLinePoints(sourcePos, targetPos);
    
    // Check if any point along the line intersects with obstacles
    for (const point of points) {
      for (const obstacle of obstacles) {
        const obstaclePos = this.getTokenCenter(obstacle, options);
        if (obstaclePos.x === point.x && obstaclePos.y === point.y) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Get all points along a line between two positions (Bresenham's algorithm)
   * @param {Object} start - Start position {x, y}
   * @param {Object} end - End position {x, y}
   * @returns {Array} - Array of points along the line
   */
  static getLinePoints(start, end) {
    const points = [];
    let x0 = Math.floor(start.x);
    let y0 = Math.floor(start.y);
    const x1 = Math.floor(end.x);
    const y1 = Math.floor(end.y);

    const dx = Math.abs(x1 - x0);
    const dy = Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx - dy;

    while (true) {
      points.push({x: x0, y: y0});

      if (x0 === x1 && y0 === y1) break;
      
      const e2 = 2 * err;
      if (e2 > -dy) {
        err -= dy;
        x0 += sx;
      }
      if (e2 < dx) {
        err += dx;
        y0 += sy;
      }
    }

    return points;
  }

  /**
   * Parse range value from various formats
   * @param {string|number} rangeValue - Range in formats like "120 feet", 120, [80, 320]
   * @returns {number|null} - Numeric range in feet, or null if unparseable
   * @date_of_change: 2025-09-11 - Fix for Magic Missile range parsing issue
   */
  static parseRangeValue(rangeValue) {
    if (Number.isFinite(rangeValue)) {
      return rangeValue;
    }

    // @date_of_change 2025-09-14 - CRITICAL FIX: Handle range objects from monster data
    if (typeof rangeValue === 'object' && rangeValue !== null) {
      // Handle range objects like {normal: 80, long: 320}
      if (Number.isFinite(rangeValue.normal)) {
        return rangeValue.normal; // Return normal range for basic parsing
      }
      if (Number.isFinite(rangeValue.short)) {
        return rangeValue.short;
      }
      // Handle other possible object formats
      if (Number.isFinite(rangeValue.value)) {
        return rangeValue.value;
      }
      if (Number.isFinite(rangeValue.range)) {
        return rangeValue.range;
      }
      if (Number.isFinite(rangeValue.maxRange)) {
        return rangeValue.maxRange;
      }
      const normalCandidate = this.parseRangeValue(
        rangeValue.normal
        ?? rangeValue.short
        ?? rangeValue.value
        ?? rangeValue.range
        ?? rangeValue.maxRange
        ?? null
      );
      if (Number.isFinite(normalCandidate)) {
        return normalCandidate;
      }
    }

    if (typeof rangeValue === 'string') {
      // Extract numeric value from strings like "120 feet", "30 ft", "Self", "Touch"
      const rangeString = rangeValue.toLowerCase().trim();

      // Handle special cases
      if (rangeString === 'self' || rangeString === 'touch') {
        return 5; // Touch range is 5 feet in D&D 5e
      }

      // Extract number from strings like "120 feet", "30 ft", "120ft"
      const numericMatch = rangeString.match(/(\d+(?:\.\d+)?)/);
      if (numericMatch) {
        const numericValue = parseFloat(numericMatch[1]);

        // Check for unit indicators (assume feet if no unit specified)
        if (rangeString.includes('mile')) {
          return numericValue * 5280; // Convert miles to feet
        } else if (rangeString.includes('yard')) {
          return numericValue * 3; // Convert yards to feet
        } else {
          return numericValue; // Assume feet (most common in D&D)
        }
      }
    }

    return null; // Unable to parse
  }

  static resolveRangeBands(rangeValue) {
    if (rangeValue == null) {
      return {
        normalRange: null,
        longRange: null,
        maxRange: null,
        hasStructuredRange: false
      };
    }

    if (Array.isArray(rangeValue)) {
      const normalRange = this.parseRangeValue(rangeValue[0]);
      const longRange = this.parseRangeValue(rangeValue[1]);
      return {
        normalRange,
        longRange,
        maxRange: Number.isFinite(normalRange) && !Number.isFinite(longRange) ? normalRange : null,
        hasStructuredRange: Number.isFinite(normalRange) || Number.isFinite(longRange)
      };
    }

    if (typeof rangeValue === 'object') {
      const normalRange = this.parseRangeValue(
        rangeValue.normal
        ?? rangeValue.short
        ?? rangeValue.base
        ?? null
      );
      const longRange = this.parseRangeValue(
        rangeValue.long
        ?? rangeValue.maximum
        ?? rangeValue.max
        ?? null
      );
      const maxRange = this.parseRangeValue(
        rangeValue.maxRange
        ?? rangeValue.rangeFeet
        ?? rangeValue.range
        ?? (Number.isFinite(normalRange) && !Number.isFinite(longRange) ? normalRange : null)
      );

      return {
        normalRange,
        longRange,
        maxRange,
        hasStructuredRange: Number.isFinite(normalRange) || Number.isFinite(longRange) || Number.isFinite(maxRange)
      };
    }

    const parsedRange = this.parseRangeValue(rangeValue);
    return {
      normalRange: null,
      longRange: null,
      maxRange: Number.isFinite(parsedRange) ? parsedRange : null,
      hasStructuredRange: Number.isFinite(parsedRange)
    };
  }

  /**
   * Validate if an attack is possible considering range, line of sight, and other factors
   * @param {Object} attacker - Attacker token
   * @param {Object} target - Target token
   * @param {Object} attack - Attack/spell definition
   * @param {Object} battleState - Current battle state with obstacles
   * @param {Object} options - Grid and calculation options
   * @returns {Object} - Validation result with detailed information
   */
  static validateAttack(attacker, target, attack, battleState = {}, options = {}) {
    const result = { valid: true, reasons: [], rangeInfo: null, hasLineOfSight: true, hasDisadvantage: false };
    // @date_of_change 2025-10-13 - POLICY: Require explicit grid context; fail-fast if missing
    const gridType = options?.gridType ?? battleState?.gridType;
    const feetPerCell = options?.feetPerCell ?? battleState?.feetPerCell;
    const gridSize = options?.gridSize ?? battleState?.gridSize;
    if (!gridType || !Number.isFinite(feetPerCell) || !Number.isFinite(gridSize)) {
      return { valid: false, reasons: ['Missing grid context (gridType/feetPerCell/gridSize)'], rangeInfo: null, hasLineOfSight: false, hasDisadvantage: false };
    }
    // @date_of_change 2025-10-14 - Always pass enriched grid options to internal helpers
    const enrichedOptions = { ...options, gridType, feetPerCell, gridSize };
    if (!target || attacker.id === target.id) {
      result.valid = false;
      result.reasons.push('Invalid target');
      return result;
    }

    // Prefer explicit type from options.attackType ('melee' | 'ranged')
    const explicitType = options?.attackType ? String(options.attackType).toLowerCase() : null;
    if (explicitType === 'melee' || explicitType === 'ranged') {
      if (explicitType === 'melee') {
        // @date_of_change 2025-10-14 - Support nested attack fields at attack.attack.*; no default reach
        const nested = attack?.attack || null;
        let weaponReach = undefined;
        if (Number.isFinite(attack?.reachFeet)) weaponReach = attack.reachFeet;
        else if (Number.isFinite(attack?.reach)) weaponReach = attack.reach;
        else if (Number.isFinite(nested?.reachFeet)) weaponReach = nested.reachFeet;
        else if (Number.isFinite(nested?.reach)) weaponReach = nested.reach;
        else if (attack?.properties?.includes?.('reach') || nested?.properties?.includes?.('reach')) weaponReach = 10;
        // Fallback: parse from string/object range fields (e.g., '5 feet') when explicit reach missing
        if (!Number.isFinite(weaponReach)) {
          const parsedReach = this.parseRangeValue(
            attack?.reachText ?? attack?.range ?? attack?.rangeFeet ?? nested?.reachText ?? nested?.range ?? nested?.rangeFeet
          );
          if (Number.isFinite(parsedReach)) weaponReach = parsedReach;
        }
        console.log(`[RangeCalculator] 🗡️ MELEE_REACH_DEBUG for "${attack?.name}":`, { weaponReach, attacker: attacker?.name, target: target?.name });
        const info = this.checkMeleeRange(attacker, target, { reachFeet: weaponReach }, enrichedOptions);
        result.rangeInfo = info;
        if (!info.inRange) {
          result.valid = false;
          result.reasons.push('Target out of melee reach');
        }
      } else {
        let normalRange, longRange, maxRange;
        const nested = attack?.attack || null;
        const rangeCandidates = [
          attack?.range,
          {
            normal: attack?.normalRange,
            long: attack?.longRange,
            maxRange: attack?.maxRange ?? attack?.rangeFeet ?? null
          },
          nested?.range,
          {
            normal: nested?.normalRange,
            long: nested?.longRange,
            maxRange: nested?.maxRange ?? nested?.rangeFeet ?? null
          }
        ];

        for (const candidate of rangeCandidates) {
          const resolved = this.resolveRangeBands(candidate);
          if (!resolved.hasStructuredRange) continue;
          normalRange = resolved.normalRange;
          longRange = resolved.longRange;
          maxRange = resolved.maxRange;
          break;
        }
        // Properties-based range hints
        if (!Number.isFinite(normalRange)) {
          const props = attack?.properties || nested?.properties;
          if (props && Array.isArray(props)) {
            const rangeProp = props.find(prop => typeof prop === 'string' && prop.toLowerCase().includes('range'));
            if (rangeProp) {
              const rangeMatch = rangeProp.match(/range\s*\((\d+)\/(\d+)\)/i);
              if (rangeMatch) {
                normalRange = parseInt(rangeMatch[1], 10);
                longRange = parseInt(rangeMatch[2], 10);
              }
            }
          }
        }
        // Weapon type fallbacks
        if (!Number.isFinite(normalRange) && (attack?.properties?.includes?.('thrown') || nested?.properties?.includes?.('thrown') || attack?.ammo || nested?.ammo)) {
          const wr = this.WEAPON_RANGES[attack.type]?.length ? this.WEAPON_RANGES[attack.type] : this.WEAPON_RANGES[nested?.type];
          if (wr) { normalRange = Array.isArray(wr) ? wr[0] : wr; longRange = Array.isArray(wr) ? wr[1] : wr * 4; }
        }
        // Parse string/object ranges generically (spells or simplified items)
        if (!Number.isFinite(normalRange)) {
          const parsedRange = this.parseRangeValue(attack?.range ?? attack?.rangeText ?? attack?.rangeFeet ?? nested?.range ?? nested?.rangeFeet);
          if (Number.isFinite(parsedRange)) {
            normalRange = parsedRange;
            if (!Number.isFinite(longRange)) {
              longRange = parsedRange; // Single-value ranges have no long band
            }
            maxRange = parsedRange;
          }
        }
        // @date_of_change 2025-09-14 - ENHANCED DEBUG: Track range validation details
        console.log(`[RangeCalculator] 🎯 RANGE_VALIDATION_DEBUG for "${attack?.name}":`, {
          attackType: 'ranged',
          normalRange,
          longRange,
          attackerName: attacker?.name,
          targetName: target?.name,
          attackerPos: attacker ? { x: attacker.x, y: attacker.y, grid: attacker.grid, gridX: attacker.gridX, gridY: attacker.gridY } : null,
          targetPos: target ? { x: target.x, y: target.y, grid: target.grid, gridX: target.gridX, gridY: target.gridY } : null,
          options
        });

        const info = this.checkRangedRange(attacker, target, { normalRange, longRange, maxRange }, enrichedOptions);
        result.rangeInfo = info;

        console.log(`[RangeCalculator] 🎯 RANGE_RESULT: ${info.inRange ? 'VALID' : 'INVALID'} - Distance: ${info.distance} ft (normal: ${normalRange}, long: ${longRange})`);

        if (!info.inRange) {
          result.valid = false;
          result.reasons.push('Target out of weapon range');
        } else if (info.hasDisadvantage) {
          result.hasDisadvantage = true;
          result.reasons.push('Attack at long range (disadvantage)');
        }
      }
    } else {
      // @date_of_change 2025-10-13 - POLICY: No heuristic classification; require explicit attackType
      result.valid = false;
      result.reasons.push('Ambiguous attack: provide explicit attackType ("melee" | "ranged") and structured reach/range');
      return result;
    }

    const obstacles = battleState.obstacles || [];
    result.hasLineOfSight = this.hasLineOfSight(attacker, target, obstacles, enrichedOptions);
    if (!result.hasLineOfSight) { result.valid = false; result.reasons.push('No line of sight to target'); }
    return result;
  }
}

export default RangeCalculator;
